/* @flow */
import _ from 'lodash';
import apiRequest from 'lib/apiRequest';
import Collection from 'lib/Collection';
import Entity from 'lib/Entity';
import EventTracking from 'lib/EventTracking';
import OfficeHours from 'entities/OfficeHours';
import Prompt from 'entities/Prompt';
import QuickPromotion from 'entities/QuickPromotion';
import { Roles } from 'user/UserConstants';
import { ReportOptions, ReportOtherOption } from 'constants/ReportConstants';

import type { DenialReason } from 'phoneCalls/components/PhoneCallModal';

// $FlowIssue
class User extends Entity {
  parse(response: Object) {
    const user = _.cloneDeep(response);
    user.__rawData = response;
    if (user['birthdate']) {
      user['birthdate'] = new Date(user['birthdate']);
    }
    if (user['recent_prompts']) {
      user['recent_prompts'] = new Collection(
        user['recent_prompts'].map(prompt => Prompt.fromJSON(prompt))
      );
    }
    if (user['prompts_inventory']) {
      user['prompts_inventory'] = new Collection(
        user['prompts_inventory'].map(prompt => Prompt.fromJSON(prompt))
      );
    } else {
      user['prompts_inventory'] = new Collection();
    }
    if (user['quick_promotions']) {
      user['quick_promotions'] = new Collection(
        user['quick_promotions'].map(prompt => QuickPromotion.fromJSON(prompt))
      );
    } else {
      user['quick_promotions'] = new Collection();
    }
    if (user['office_hours']) {
      user['office_hours'] = OfficeHours.fromJSON(user['office_hours']);
    }
    if (user['needs']) {
      // UNTIL https://github.com/remind101/r101-api/pull/8023/
      delete user['needs']['chat_promotion'];
    }
    if (user['created_at']) {
      user['created_at'] = new Date(user['created_at']);
    }
    if (user['muted_until']) {
      user['muted_until'] = new Date(user['muted_until']);
    }
    if (user.profilePictureUrl) {
      user['profile_picture_url'] = user.profilePictureUrl;
    }

    if (user.firstName) {
      user['first_name'] = user.firstName;
    }

    if (user.lastName) {
      user['last_name'] = user.lastName;
    }

    return user;
  }

  urlRoot() {
    return '/user';
  }

  defaults() {
    return {
      experiments: {},
    };
  }

  getUuid() {
    return this.get('uuid');
  }

  getPrefix(): ?string {
    return this.get('prefix');
  }

  getFirstName(): string {
    return this.get('first_name') || '';
  }

  getCountryCode(): ?string {
    return this.get('country_code');
  }

  getCreatedAt() {
    return this.get('created_at');
  }

  hasManualInviteSupported() {
    return this.get('manual_invite_supported');
  }

  hasEmail(): boolean {
    return !!this.getEmail();
  }

  hasSmsDevice(): boolean {
    return !!this.get('default_phone_number');
  }

  getInitials() {
    return this.get('initials');
  }

  getProfilePictureUrl(): ?string {
    return this.get('profile_picture_url');
  }

  getGradeLevel() {
    return this.get('grade_level');
  }

  hasSmsSupported() {
    return this.get('sms_supported');
  }

  getLastName() {
    return this.get('last_name') || '';
  }

  getUnconfirmedEmail() {
    return this.get('unconfirmed_email');
  }

  getReferralUrl() {
    return this.get('referral_url');
  }

  getOrganizationId() {
    return this.get('organization_id');
  }

  getContactabilityStatus() {
    return this.get('contactability_status');
  }

  // There is an issue adding organization_ids as an empty array to the user factory for tests:
  // https://github.com/Coffa/factory_girl/issues/20
  getOrganizationIds() {
    return this.get('organization_ids') || [];
  }

  getEmail() {
    return this.get('email');
  }

  getDisplayName() {
    return this.get('display_name');
  }

  getFullName() {
    return `${this.getFirstName()} ${this.getLastName()}`;
  }

  getName() {
    return this.get('name') || this.getFullName();
  }

  getColor() {
    return this.get('color');
  }

  getBirthdate() {
    return this.get('birthdate');
  }

  getIsChild() {
    return this.get('is_child');
  }

  getRole() {
    return this.get('role');
  }

  getUserSelectedRole() {
    return this.get('user_selected_role');
  }

  getLocale() {
    return this.get('locale') || 'en-US';
  }

  getISO639Locale() {
    return this.getLocale().split('-')[0];
  }

  getSignature(): string {
    return this.get('signature');
  }

  hasPassword() {
    return this.get('has_password');
  }

  getPusherChannel() {
    return this.get('pusher_channel');
  }

  getAge() {
    const birthdate = this.getBirthdate();
    if (!birthdate) {
      return null;
    }

    const today = new Date();
    const age = today.getFullYear() - birthdate.getFullYear();
    const m = today.getMonth() - birthdate.getMonth();
    return m < 0 || (m === 0 && today.getDate() < birthdate.getDate()) ? age - 1 : age;
  }

  isUnderage() {
    const age = this.getAge();
    return typeof age === 'number' && age < 13;
  }

  getParentEmail() {
    return this.get('parent_email');
  }

  getCanPhone() {
    return this.get('can_phone');
  }

  getCanPhoneDenialReason(): DenialReason {
    return this.get('can_phone_denial_reason');
  }

  /**
   * Memoize so that only first exposure logs (subsequent are redundant).
   *
   * Note that our experiment reporting framework requires than an impression
   * is logged at least once per day. We rely on the refreshAppIfOld() in src/app
   * to ensure that the app is reloaded every 24hrs.
   */
  getExperimentVariant: Function = _.memoize(experiment => {
    const variant = this.get('experiments')[experiment];
    if (variant) {
      const experimentsTrackable = this.get('experiments_trackable') || [];
      if (experimentsTrackable.find(e => e === experiment)) {
        this.trackExperimentExposure(experiment, variant);
      }
      return variant;
    } else {
      return 'control';
    }
  });

  // TODO type these against our experiment definition
  trackExperimentExposure(experiment: string, variant: string) {
    EventTracking.experimentExposure(experiment, variant);
  }

  getFeatures() {
    return this.get('features');
  }

  hasFeature(name: string) {
    return !!this.get('features')[name];
  }

  getReachability() {
    return this.get('reachability');
  }

  getReachabilityState() {
    return this.get('reachability_state');
  }

  /**
   * @deprecated in favor of prompts_inventory
   * @see https://github.com/remind101/r101-frontend/pull/2607#issuecomment-212069295
   */
  getRecentPrompts() {
    return this.get('recent_prompts');
  }

  /**
   * Inventory of prompts we would like to show the user
   * @return {[Prompt]}
   */
  getPromptsInventory(): Collection<Prompt> {
    // TODO figure out why this is undefined sometimes
    return this.get('prompts_inventory') || new Collection();
  }

  getQuickPromotions() {
    return this.get('quick_promotions');
  }

  removeQuickPromotion(promotion: QuickPromotion) {
    this.get('quick_promotions').remove(promotion);
  }

  hasItemInPromptsInventory(name: string): boolean {
    return !!this.getPromptsInventory().find(item => item.get('action') === name);
  }

  findPrompt(name: string): ?Prompt {
    return this.getPromptsInventory().find(item => item.get('action') === name);
  }

  getOfficeHours() {
    return this.get('office_hours');
  }

  getNeeds() {
    return this.get('needs');
  }

  completedSetup() {
    return !_.some(this.getNeeds());
  }

  getTrackingSegment() {
    const email = this.getEmail();
    if (email && email.endsWith('remind101.com')) {
      return 'remind';
    } else if (this.get('is_admin')) {
      return 'admin';
    } else if (this.get('is_child')) {
      return 'child';
    } else {
      return this.getRole();
    }
  }

  isOrInferredChild(): boolean {
    return !!this.get('is_or_inferred_child');
  }

  isAdmin(): boolean {
    return !!this.get('is_admin');
  }

  // $FlowFixMe
  setOfficeHours(hours) {
    return this.set({ office_hours: hours });
  }

  getGroupChatMemberLimit() {
    return this.get('settings')['chats']['members']['limit'];
  }

  getAnnouncementCharactersLimit() {
    return this.get('settings')['messages']['characters']['limit'];
  }

  getAnnouncementCharactersLimitWithFiles() {
    return this.get('settings')['messages']['characters']['limit_with_files'];
  }

  hasPaidLongCharacterLimit() {
    return this.get('settings')['messages']['has_paid_long_character_limit'];
  }

  needsGroups() {
    return this.getNeeds()['groups'];
  }

  getSignInCount() {
    return this.get('sign_in_count');
  }

  isOrganizationPickerEligible() {
    return this.get('organization_picker_eligible');
  }

  getPreferences() {
    return this.get('preferences');
  }

  getPreference(name: string) {
    return this.get('preferences')[name];
  }

  // $FlowFixMe
  setPreferences(preferences) {
    return this.set({ preferences });
  }

  worksAtASchool() {
    return this.getPreferences()['works_at_a_school'];
  }

  isRCE() {
    return !!this.get('is_rce');
  }

  isTeacher() {
    return this.getRole() === Roles.teacher;
  }

  isStudent() {
    return this.getRole() === Roles.student;
  }

  isBlocked() {
    return this.get('blocked');
  }

  getPermissions() {
    return this.get('permissions') || {};
  }

  canAddProfilePicture(): boolean {
    return !!this.getPermissions()['can_add_profile_picture'];
  }

  canChat(): boolean {
    return !!this.getPermissions()['can_chat'];
  }

  canCollectMoney(): boolean {
    return !!this.getPermissions()['can_collect_money'];
  }

  canSendMoney(): boolean {
    return !!this.getPermissions()['can_send_money'];
  }

  canComposeAnnouncements(): boolean {
    return !!this.getPermissions()['can_compose_announcements'];
  }

  canComposeGroupChat(): boolean {
    return !!this.getPermissions()['can_compose_group_chat'];
  }

  canComposeIndividualChat(): boolean {
    return true;
  }

  canCreateGroups(): boolean {
    return !!this.getPermissions()['can_create_group'];
  }

  canOwnGroups(): boolean {
    return !!this.getPermissions()['can_own_groups'];
  }

  canCallFromDialer(): boolean {
    return !!this.getPermissions()['can_call_from_dialer'];
  }

  cannotCloseAccountReasons(): string[] {
    return this.get('cannot_close_account_reasons');
  }

  getMutedUntil(): ?Date {
    return this.get('muted_until');
  }

  isMuted(): boolean {
    const mutedUntil = this.getMutedUntil();
    return !!mutedUntil && mutedUntil >= new Date();
  }

  savePreference(name: string, value: mixed) {
    return apiRequest(`/users/${this.id}/prefs`, 'PATCH', {
      preferences: { [name]: value },
    }).then(({ preferences }) => {
      this.set({ preferences });
      return preferences;
    });
  }

  static setRole(role: string): Promise<User> {
    return apiRequest('/user', 'PATCH', { user: { role } }).then(user => User.fromJSON(user));
  }

  static resetPasswordWithToken(password: string, token: string): Promise<User> {
    const params = {
      password,
      password_confirmation: password,
      email_verification: {
        token,
      },
    };

    return apiRequest('/password', 'PUT', params).then(user => User.fromJSON(user));
  }

  static resetPasswordWithCode(password: string, code: string, address: string): Promise<User> {
    const params = {
      password,
      password_confirmation: password,
      sms_verification: {
        address,
        code,
      },
    };

    return apiRequest('/password', 'PUT', params).then(user => User.fromJSON(user));
  }

  static delete(token: ?string, reason: ?string) {
    const params = {};

    if (token) {
      params.token = token;
    }

    if (reason) {
      params.reason = reason;
    }

    return apiRequest('/user', 'DELETE', params);
  }

  static verifyDeleteToken(token: string) {
    return apiRequest('/account_deletion', 'GET', { token });
  }

  /* Api send 202 even when error happens
   * we need a custom promise the handle this.
   */
  // $FlowFixMe missing annotation
  static create(attr): Promise<User> {
    return new Promise((resolve, reject) => {
      const onSuccess = result => {
        // if there an id it's probably a user.
        if (result.id) {
          resolve(User.fromJSON(result));
          return;
        }

        // otherwise it's an error
        reject(result);
      };

      apiRequest('/users', 'POST', { user: attr }).then(onSuccess, error => reject(error));
    });
  }

  static update(attr: Object): Promise<User> {
    return apiRequest('/user', 'PATCH', { user: attr }).then(response => User.fromJSON(response));
  }

  static confirm(confirmationToken: string, source: string): Promise<User> {
    return apiRequest('/users/confirm', 'POST', {
      confirmation_token: confirmationToken,
      source,
    }).then(user => User.fromJSON(user));
  }

  static requestConfirmation() {
    return apiRequest('/users/confirm/request', 'POST');
  }

  static setBirthdate(birthdate: Date): Promise<User> {
    const dateString =
      birthdate.getFullYear() +
      '-' +
      ('0' + (birthdate.getMonth() + 1)).slice(-2) +
      '-' +
      ('0' + birthdate.getDate()).slice(-2);
    return apiRequest('/user', 'PATCH', {
      user: { birthdate: dateString },
    }).then(user => User.fromJSON(user));
  }

  static setParentEmail(parentEmail: string): Promise<User> {
    return apiRequest('/user', 'PATCH', {
      user: { parent_email: parentEmail },
    }).then(user => User.fromJSON(user));
  }

  static requestForPublicGroups(teacherId: Backbone$Id) {
    return apiRequest(`/users/${teacherId}/request_for_public_groups`, 'POST');
  }

  // DELETEME not used
  // $FlowFixMe
  static loadProfile(opts) {
    return apiRequest('/profiles', 'GET', opts);
  }

  static reportUser(userId: Backbone$Id, key: string, notes: string) {
    const reason = ReportOptions[key] || ReportOtherOption;
    const reasonAndNotes = notes ? `${reason} - ${notes}` : reason;
    return apiRequest(`/users/${userId}/report`, 'POST', {
      key,
      reason: reasonAndNotes,
    });
  }

  static addParent(name: string, address: string): Promise<User> {
    return apiRequest('/users/parents', 'POST', {
      address,
      name,
    }).then(response => User.fromJSON(response.child));
  }
}

export default User;
